import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { createButtonPressAnimation, attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";
import { incrementBet, closeMenu, closeSymbolPopup } from "../../../../State/actions";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import {
  selectIsSpinning,
  selectUserInterfaceDisabled,
  selectSessionExpired,
  selectBet,
  selectBets,
  selectBalance,
  selectAutoPlayActive,
  selectHoldAndWinActive,
  selectFreeRoundsActive,
} from "../../../../State/selectors";

const ButtonIncrementBet = ({ x = 0, y = 0 }) => {
  const dispatch = useDispatch();
  const { getResponsiveConfig, device } = useDimensions();

  const isSpinning = useSelector(selectIsSpinning);
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const balance = useSelector(selectBalance);
  const sessionExpired = useSelector(selectSessionExpired);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  const texture = PIXI.Assets.get(ASSET_NAME.icon_plus);

  // Memoize calculations
  const currentBetIndex = useMemo(() => bets.indexOf(bet), [bets, bet]);
  const nextBetAmount = useMemo(
    () => bets[currentBetIndex + 1],
    [bets, currentBetIndex]
  );

  const canIncreaseBet = useMemo(
    () =>
      currentBetIndex < bets.length - 1 &&
      balance >= nextBetAmount &&
      !isSpinning &&
      !sessionExpired &&
      !autoPlayActive &&
      !userInterfaceDisabled &&
      !holdAndWinActive &&
      !(FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive),
    [
      currentBetIndex,
      bets.length,
      balance,
      nextBetAmount,
      isSpinning,
      sessionExpired,
      autoPlayActive,
      userInterfaceDisabled,
      holdAndWinActive,
      freeRoundsActive,
    ]
  );

  // Get responsive configuration from context - memoized
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Memoize button radius calculation
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  // Memoize hitArea calculation
  const hitArea = useMemo(
    () => new PIXI.Circle(buttonRadius, 0, buttonRadius),
    [buttonRadius]
  );

  const handleClick = useCallback(() => {
    // Close menu if open
    dispatch(closeMenu());
    dispatch(closeSymbolPopup());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    if (canIncreaseBet) {
      soundManager.play(SOUND_NAME.button_click);

      // Add press animation before incrementing bet
      if (buttonSpriteRef.current && !isPressAnimatingRef.current) {
        isPressAnimatingRef.current = true;
        createButtonPressAnimation(buttonSpriteRef.current, {
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height,
          onComplete: () => {
            isPressAnimatingRef.current = false;
          }
        });
      }

      dispatch(incrementBet());
    }
  }, [canIncreaseBet, dispatch, device, buttonConfig.width, buttonConfig.height]);

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (canIncreaseBet && buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [canIncreaseBet]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, []);

  // Clean up hover effects on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
    };
  }, []);

  // Memoize draw function for Graphics component
  const drawGraphics = useCallback(
    (g) => {
      g.clear();
      // Nearly invisible fill (required for hit detection to work)
      g.beginFill(0x000000, 0.01);
      g.drawCircle(buttonRadius, 0, buttonRadius);
      g.endFill();
    },
    [buttonRadius]
  );

  return (
    <Container x={x} y={y}>
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="increment_bet_button_interactive_area"
        eventMode={canIncreaseBet ? "static" : "none"}
        cursor={canIncreaseBet ? "pointer" : "default"}
        hitArea={hitArea}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={drawGraphics}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={buttonSpriteRef}
        texture={texture}
        name="secondary_button_increment_bet"
        anchor={{ x: 0, y: 0.5 }}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={canIncreaseBet ? 1 : 0.5}
      />
    </Container>
  );
};

export default ButtonIncrementBet;
